import { useState } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import type { PurchasePackageViewModel } from '../PackagesSection.helpers';
import {
  GET_INVOICE_PRECREATE_ANALYZE,
  type InvoicePrecreateAnalyzeResponse,
  type InvoicePrecreateWarning,
} from '../api';
import { useInvoicePayment } from './useInvoicePayment';
import { CREATE_INVOICE } from '@/api/mutations/payments.mutations';
import {
  CreateInvoiceResponse,
  CreateInvoiceVariables,
  Invoice,
  InvoicePeriod,
  InvoiceStartsAt,
} from '@/api/types/payments.types';
import { useToastStore } from '@/store/useToastStore';
import { useToggleOpenState } from '@/hooks/useToggleOpenState';
import {
  useBusinessBalance,
  useCurrentUser,
} from '@/features/Profile/core/hooks';

const INVOICE_PERIOD_BY_DAYS: Partial<Record<number, InvoicePeriod>> = {
  30: InvoicePeriod.DAYS_30,
  60: InvoicePeriod.DAYS_60,
  90: InvoicePeriod.DAYS_90,
  180: InvoicePeriod.DAYS_180,
};

export function usePackageInvoice(
  item: PurchasePackageViewModel,
  onPaymentSuccess: () => void
) {
  const { t } = useTranslation();
  const { loading: isCurrentUserLoading, user } = useCurrentUser();
  const {
    cashBalance,
    loading: isBalanceLoading,
    refetchBalance,
  } = useBusinessBalance();
  const openToast = useToastStore(state => state.openToast);
  const [startsAt, setStartsAt] = useState<InvoiceStartsAt>(
    InvoiceStartsAt.TODAY
  );
  const [invoiceWarning, setInvoiceWarning] =
    useState<InvoicePrecreateWarning | null>(null);
  const [isPaymentOpen, , openPayment, closePayment] = useToggleOpenState();
  const [createInvoiceMutation, { loading: isCreatingInvoice }] = useMutation<
    CreateInvoiceResponse,
    CreateInvoiceVariables
  >(CREATE_INVOICE, { errorPolicy: 'all' });
  const [analyzeInvoice, { loading: isAnalyzingInvoice }] = useLazyQuery<
    InvoicePrecreateAnalyzeResponse,
    CreateInvoiceVariables
  >(GET_INVOICE_PRECREATE_ANALYZE, {
    fetchPolicy: 'no-cache',
    nextFetchPolicy: 'standby',
  });
  const invoicePayment = useInvoicePayment({
    amount: item.price,
    getInvoice: createInvoice,
    isAppleGooglePayEnabled: user.isAppleGooglePayEnabled,
    isOpen: isPaymentOpen,
    onBalanceTopUpSuccess: () => {
      refetchBalance();
    },
    onClose: closePayment,
    onSuccess: onPaymentSuccess,
    paymentMethods: user.paymentMethods,
    serviceName: item.name,
  });
  const paymentPackage = invoicePayment.invoice
    ? { ...item, price: invoicePayment.invoice.amount }
    : item;

  const isProcessingPayment =
    isAnalyzingInvoice ||
    isCreatingInvoice ||
    invoicePayment.isProcessingPayment;
  const isNullifyWarningOpen = invoiceWarning !== null;

  function showError(message = t('common.something_went_wrong')) {
    openToast({ text: message, size: 'compact', borderRadius: 14 });
  }

  function getInvoiceVariables(): CreateInvoiceVariables | null {
    const period = INVOICE_PERIOD_BY_DAYS[item.periodDays];
    if (!period) {
      showError();
      return null;
    }

    return { period, startsAt, tariffPlanTitle: item.name };
  }

  async function createInvoice(): Promise<Invoice | null> {
    const variables = getInvoiceVariables();
    if (!variables) return null;

    try {
      const response = await createInvoiceMutation({
        variables,
      });
      const result = response.data?.createInvoice;
      if (result?.errors.length) {
        showError(result.errors[0].message);
        return null;
      }
      if (!result?.entity) {
        showError();
        return null;
      }
      return result.entity;
    } catch {
      showError();
      return null;
    }
  }

  async function createInvoiceAndPayment() {
    if (isProcessingPayment) return;
    closeNullifyWarning();
    await invoicePayment.payInvoice();
  }

  function closeNullifyWarning() {
    setInvoiceWarning(null);
  }

  async function getInvoiceAnalysis(variables: CreateInvoiceVariables) {
    try {
      const response = await analyzeInvoice({ variables });
      const analysis = response.data?.invoicePrecreateAnalyze;
      if (!analysis) {
        showError();
        return null;
      }
      return analysis;
    } catch {
      showError();
      return null;
    }
  }

  async function handlePay() {
    if (isProcessingPayment) return;

    if (invoicePayment.invoice) {
      await createInvoiceAndPayment();
      return;
    }

    const variables = getInvoiceVariables();

    if (!variables) return;

    const analysis = await getInvoiceAnalysis(variables);

    if (!analysis) return;

    if (analysis.hasWarning) {
      setInvoiceWarning(analysis.warning);
      return;
    }

    await createInvoiceAndPayment();
  }

  function selectStartsAt(value: InvoiceStartsAt) {
    if (value === startsAt) return;

    invoicePayment.resetInvoice();
    setStartsAt(value);
  }

  return {
    cashBalance,
    closeNullifyWarning,
    closePayment,
    createInvoiceAndPayment,
    paymentPackage,
    handlePay,
    invoicePayment,
    invoiceWarning,
    isActivationContinueDisabled: isCurrentUserLoading || isBalanceLoading,
    isNullifyWarningOpen,
    isPaymentOpen,
    isProcessingPayment,
    openPayment,
    selectStartsAt,
    startsAt,
  };
}
